import React, { useEffect, useState } from "react";
import {
  Checkbox,
  Button,
  FormControlLabel,
  useMediaQuery,
  TextareaAutosize,
  Snackbar,
  Alert,
  Slide,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import AnseraLogo from "assets/images/Anser.png";
import { useSettings } from "context/SettingsContext";

const DefaultQuestions = () => {
  const [enabled, setEnabled] = useState(false);
  const [questions, setQuestions] = useState(["", "", "", "", ""]);
  const [originalQuestions, setOriginalQuestions] = useState(["", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const labelWidth = isMobile ? "100%" : "240px";
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const { settings, updateSettings, loading: settingsLoading } = useSettings();

  useEffect(() => {
    if (settings) {
      try {
        const questionsFromSettings = settings.ansera_search_default_questions;
        let parsedQuestions = ["", "", "", "", ""];
        
        if (questionsFromSettings) {
          if (typeof questionsFromSettings === 'string') {
            parsedQuestions = JSON.parse(questionsFromSettings);
          } else if (Array.isArray(questionsFromSettings)) {
            parsedQuestions = questionsFromSettings;
          }
          while (parsedQuestions.length < 5) {
            parsedQuestions.push("");
          }
          if (parsedQuestions.length > 5) {
            parsedQuestions = parsedQuestions.slice(0, 5);
          }
        }
        
        setQuestions(parsedQuestions);
        setEnabled(settings.ansera_search_display_default_questions === 1);
        setOriginalQuestions([...parsedQuestions]);
      } catch (error) {
        console.error("Error parsing questions:", error);
        const defaultQuestions = ["", "", "", "", ""];
        setQuestions(defaultQuestions);
        setOriginalQuestions([...defaultQuestions]);
        setEnabled(settings.ansera_search_display_default_questions === 1);
      }
    }
  }, [settings]);

  const handleQuestionChange = (index, value) => {
    const updated = [...questions];
    updated[index] = value;
    setQuestions(updated);
  };

  const hasChanges = JSON.stringify(questions) !== JSON.stringify(originalQuestions) || 
                    enabled !== (settings?.ansera_search_display_default_questions === 1);

  const handleSave = async () => {
    try {
      setLoading(true);
      const questionsToSave = Array.isArray(questions) ? questions : [];
      const updates = {
        ansera_search_default_questions: JSON.stringify(questionsToSave),
        ansera_search_display_default_questions: enabled ? 1 : 0,
      };

      await updateSettings(updates);
      
      setOriginalQuestions([...questions]);
      setSnackbarMessage("Questions updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to save settings:", error);
      setSnackbarMessage("Failed to save Questions!");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  if (settingsLoading && !settings) {
    return (
      <SoftBox
        sx={{
          backgroundColor: "#fff",
          p: isMobile ? 2 : 4,
          borderRadius: "16px",
          boxShadow: "0px 6px 20px rgba(0,0,0,0.06)",
          textAlign: "center",
          py: 4,
        }}
      >
        <SoftTypography>Loading settings...</SoftTypography>
      </SoftBox>
    );
  }

  if (!Array.isArray(questions)) {
    console.error("Questions is not an array:", questions);
    setQuestions(["", "", "", "", ""]);
    return null; 
  }

  return (
    <SoftBox
      sx={{
        backgroundColor: "#fff",
        p: isMobile ? 2 : 4,
        borderRadius: "16px",
        boxShadow: "0px 6px 20px rgba(0,0,0,0.06)",
      }}
      display="flex"
      flexDirection="column"
      gap={2.5}
    >
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={(props) => <Slide {...props} direction="top" />}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: "100%",
            color: "#ffffff",
            backgroundColor:
              snackbarSeverity === "success"
                ? "#008080" 
                : snackbarSeverity === "error"
                ? "#d32f2f"
                : undefined,
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <SoftBox display="flex" alignItems="center" gap={1}>
        <img src={AnseraLogo} alt="Ansera Logo" width={64} />
        <SoftTypography variant="h6" fontWeight="bold">
          Default Questions
        </SoftTypography>
      </SoftBox>

      <SoftTypography
        variant="body2"
        color="text.secondary"
        sx={{
          borderBottom: "1px solid #e0e0e0",
          pb: 1,
          mb: 1,
        }}
      >
        Display up to 5 default search questions that will appear on a visitor's first interaction.
      </SoftTypography>

      <SoftBox
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        alignItems="center"
        gap={1.5}
      >
        <SoftBox width={labelWidth}>
          <SoftTypography variant="button" fontWeight="medium">
            Display Default Questions
          </SoftTypography>
        </SoftBox>
        <FormControlLabel
          control={
            <Checkbox
              checked={enabled}
              onChange={() => setEnabled((prev) => !prev)}
            />
          }
          label={enabled ? "Enabled" : "Disabled"}
        />
      </SoftBox>

      <SoftBox>
        <SoftTypography variant="button" fontWeight="medium" mb={1}>
          Questions:
        </SoftTypography>
        <SoftBox mt={1} display="flex" flexDirection="column" gap={2}>
          {questions.map((q, i) => (
            <TextareaAutosize
              key={i}
              value={q}
              onChange={(e) => handleQuestionChange(i, e.target.value)}
              placeholder={`Question ${i + 1}`}
              minRows={2}
              style={{
                width: "100%",
                fontSize: "14px",
                fontFamily: `"Roboto","Helvetica","Arial",sans-serif`,
                padding: "8px",
                borderRadius: "10px",
                border: "1px solid #ccc",
                outline: "none",
                resize: "vertical",
                boxShadow: "inset 0 1px 2px rgba(0,0,0,0.1)",
                transition: "border-color 0.3s ease",
              }}
              onFocus={(e) => (e.target.style.border = "1px solid #219297")}
              onBlur={(e) => (e.target.style.border = "1px solid #ccc")}
            />
          ))}
        </SoftBox>
      </SoftBox>

      <SoftBox mt={2}>
        <Button
          variant="contained"
          size="medium"
          disabled={!hasChanges || loading || settingsLoading}
          onClick={handleSave}
          sx={{
            backgroundColor: "#219297",
            color: "#fff",
            fontWeight: 600,
            textTransform: "none",
            px: 3,
            py: 1,
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: "#1a767a",
            },
            opacity: !hasChanges || loading ? 0.5 : 1,
            cursor: !hasChanges || loading ? "not-allowed" : "pointer",
          }}
        >
          {loading ? "Saving..." : "Save Changes"}
        </Button>
      </SoftBox>
    </SoftBox>
  );
};

export default DefaultQuestions;